import {
  BelpostRepublicAddress,
  BelpostShortRepublicAddress,
} from '$shared/types/belpost-republic-address';
import {
  BelpostInternationalAddress,
  BelpostShortInternationalAddress,
} from '$shared/types/belpost-international-address';
import {
  BelpostContact,
  BelpostShortContact,
} from '$shared/types/belpost-contact';
import { Injectable } from '@angular/core';
import { ContactAddressType } from '../model/address-map-type';

/**
 * Сервис-адаптер для преобразования BelpostContact в BelpostShortContact.
 * Обеспечивает трансформацию полных данных в сокращенную форму для отправки на бекенд.
 */
@Injectable({ providedIn: 'root' })
export class BelpostToShortContactAdapterService {
  /**
   * Преобразует полный контакт в сокращенную форму.
   *
   * @param fullContact - Полный контакт из API
   * @returns Сокращенный контакт для формы
   */
  public transform(fullContact: BelpostContact | null): BelpostShortContact {
    if (!fullContact) {
      return null;
    }
    return {
      person: fullContact.person,
      address: this.transformAddress(fullContact.address),
    };
  }

  /**
   * Преобразует адрес в сокращенную форму.
   *
   * @param fullAddress - Полный адрес из API
   * @returns Сокращенный адрес для формы
   */
  public transformAddress(
    fullAddress: BelpostRepublicAddress | BelpostInternationalAddress
  ): BelpostShortRepublicAddress | BelpostShortInternationalAddress {
    if (this.isRepublicAddress(fullAddress)) {
      return this.transformRepublicAddress(fullAddress);
    } else {
      return this.transformInternationalAddress(fullAddress);
    }
  }

  /**
   * Проверяет, является ли адрес республиканским (белорусским).
   * Республиканские адреса имеют external_id, международные - нет.
   *
   * @param address - Адрес для проверки
   * @returns true, если адрес республиканский
   */
  private isRepublicAddress(
    address: BelpostRepublicAddress | BelpostInternationalAddress
  ): address is BelpostRepublicAddress {
    return 'external_id' in address;
  }

  /**
   * Преобразует международный адрес в сокращенную форму.
   *
   * @param internationalAddress - Международный адрес из API
   * @returns Сокращенный международный адрес для формы
   */
  private transformInternationalAddress(
    internationalAddress: BelpostInternationalAddress
  ): BelpostShortInternationalAddress {
    return {
      type: 'address' as Extract<ContactAddressType, 'address'>,
      country_code: internationalAddress.country.code,
      city: internationalAddress.city,
      address_line: internationalAddress.address_line,
      postcode: internationalAddress.postcode,
      flat: internationalAddress.flat ?? null,
    };
  }

  /**
   * Преобразует республиканский адрес в сокращенную форму.
   *
   * @param republicAddress - Республиканский адрес из API
   * @returns Сокращенный республиканский адрес для формы
   */
  public transformRepublicAddress(
    republicAddress: BelpostRepublicAddress
  ): BelpostShortRepublicAddress {
    return {
      id: republicAddress.external_id,
      country_code: republicAddress.country_code,
      type: republicAddress.type,
      house: republicAddress.house || '',
      cell_number: republicAddress.cell_number
        ? republicAddress.cell_number.toString()
        : null,
      block: republicAddress.block || null,
      flat: republicAddress.flat || null,
    };
  }
}
